/**
 * cacheManager.js — sessionStorage-backed API GET cache with custom TTLs.
 *
 * TTL resolution order (highest → lowest priority):
 *   1. User prefs (localStorage `api_cache_user_prefs`)
 *   2. Admin defaults (localStorage `api_cache_admin_defaults`)
 *   3. Hardcoded DEFAULT_TTLS
 *
 * No-cache patterns (real-time data) always bypass the cache regardless.
 */
import axios from 'axios';

const CACHE_PREFIX = 'api_cache_';
const USER_TTL_KEY  = 'api_cache_user_prefs';    // localStorage — survives sessions
const ADMIN_TTL_KEY = 'api_cache_admin_defaults'; // localStorage — admin-set defaults

// ─── Default TTLs (minutes) ──────────────────────────────────────────────────
export const DEFAULT_TTLS = {
  '/targets':         3,
  '/users':          10,
  '/business-places': 10,
  '/dashboard':       5,
  '/revenue':         5,
  '/payout':          2,
  '/hr':              5,
  '/margin':          5,
  '/visits':          5,
  '/leads':           5,
  '/sales-points':   10,
  '/business-lines': 10,
  '/roles':          15,
  '/features':       15,
  '/expense':         5,
  '/travel':          5,
  '/inventory':       5,
  '/approvals':       5,
  '/designations':   15,
  '/settings':       10,
};

// Section labels for UI display
export const SECTION_LABELS = {
  '/targets':         'Targets',
  '/users':           'Users',
  '/business-places': 'Business Places',
  '/dashboard':       'Dashboard',
  '/revenue':         'Revenue',
  '/payout':          'Payout',
  '/hr':              'HR',
  '/margin':          'Margin',
  '/visits':          'Visits',
  '/leads':           'Leads',
  '/sales-points':    'Sales Points',
  '/business-lines':  'Business Lines',
  '/roles':           'Roles & Features',
  '/features':        'Features',
  '/expense':         'Expenses',
  '/travel':          'Travel Plans',
  '/inventory':       'Inventory',
  '/approvals':       'Approvals',
  '/designations':    'Designations',
  '/settings':        'Settings',
};

// Patterns that must never be cached (real-time / auth / polling)
const NO_CACHE_PATTERNS = [
  '/task/',
  'sync-status',
  'bootstrap',
  'validate-session',
  'bulk-apply-status',
  '/auth/',
  'resolve-all-status',
  'refresh-status',
  'pos-cache-status',
  'master-cache-status',
  'data-health',
  'reporting-map-status',
  // Cache management UI needs real-time slot status — never cache these
  'chunks-plan',
  'cache/audit-log',
  // System-mapped report is memoised server-side + uses stale-while-revalidate;
  // client caching it here only made the on-screen numbers stale after a refresh.
  'system-mapped-report',
];

// ─── TTL helpers ─────────────────────────────────────────────────────────────

export function getCacheTTLs() {
  try {
    const userPrefs = JSON.parse(localStorage.getItem(USER_TTL_KEY) || 'null');
    if (userPrefs) return { ...DEFAULT_TTLS, ...userPrefs };
    const adminDefaults = JSON.parse(localStorage.getItem(ADMIN_TTL_KEY) || 'null');
    if (adminDefaults) return { ...DEFAULT_TTLS, ...adminDefaults };
  } catch { /* ignore */ }
  return { ...DEFAULT_TTLS };
}

export function setUserCacheTTLs(config) {
  try { localStorage.setItem(USER_TTL_KEY, JSON.stringify(config)); } catch { /* ignore */ }
}

export function setAdminCacheTTLs(config) {
  try { localStorage.setItem(ADMIN_TTL_KEY, JSON.stringify(config)); } catch { /* ignore */ }
}

export function resetUserCacheTTLs() {
  localStorage.removeItem(USER_TTL_KEY);
}

// Returns TTL in ms for a given URL, 0 means no-cache
function getTTLMs(url) {
  const path = url.replace(/^https?:\/\/[^/]+/, '').replace(/^\/api/, '');
  for (const p of NO_CACHE_PATTERNS) {
    if (path.includes(p)) return 0;
  }
  const ttls = getCacheTTLs();
  for (const prefix of Object.keys(DEFAULT_TTLS)) {
    if (path.startsWith(prefix)) {
      const minutes = ttls[prefix] !== undefined ? ttls[prefix] : DEFAULT_TTLS[prefix];
      return minutes * 60 * 1000;
    }
  }
  return 5 * 60 * 1000; // default 5 min
}

// ─── Cache key ───────────────────────────────────────────────────────────────

function buildCacheKey(url, params) {
  const paramStr = params
    ? new URLSearchParams(
        Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== null)
          .map(([k, v]) => [k, String(v)])
          .sort(([a], [b]) => a.localeCompare(b))
      ).toString()
    : '';
  // The Customer Onboarding category + process-type are appended to every
  // /revenue/ GET by an axios REQUEST interceptor (see Revenue.js), AFTER this
  // key is computed — so they'd otherwise be missing from the key and toggling
  // FASTag↔Prepaid would return the other category's cached response. Fold them
  // in here (read straight from localStorage — no import needed) so each
  // category/process-type has its own cache entry.
  let extra = '';
  try {
    if (url.includes('/revenue/')) {
      const cat = localStorage.getItem('revenue_category') || 'fastag';
      const pt = localStorage.getItem('revenue_process_type') || '';
      extra = `#cat=${cat}${pt ? `&pt=${pt}` : ''}`;
    }
  } catch { /* localStorage unavailable — ignore */ }
  return `${CACHE_PREFIX}${url}${paramStr ? '?' + paramStr : ''}${extra}`;
}

// ─── Main cached GET ─────────────────────────────────────────────────────────

export async function cachedAxiosGet(url, config = {}) {
  const ttl = getTTLMs(url);

  // Skip cache for no-cache patterns — behave exactly like axios.get
  if (ttl === 0) return axios.get(url, config);

  const key = buildCacheKey(url, config?.params);

  try {
    const raw = sessionStorage.getItem(key);
    if (raw) {
      const { data, expiresAt } = JSON.parse(raw);
      if (Date.now() < expiresAt) {
        return { data, status: 200, _fromCache: true };
      }
    }
  } catch { /* corrupt entry — fall through to network */ }

  const result = await axios.get(url, config);

  // Don't cache placeholder / "still building" responses — they would freeze
  // the UI in an empty state until the TTL expires even after the real data lands.
  const resData = result.data;
  if (resData && typeof resData === 'object' &&
      (resData._cache_building || resData._awaiting_initial_build || resData._cache_refreshing)) {
    return result;
  }

  try {
    sessionStorage.setItem(key, JSON.stringify({ data: result.data, expiresAt: Date.now() + ttl }));
  } catch {
    // sessionStorage quota exceeded — evict oldest and retry once
    evictOldestEntries();
    try {
      sessionStorage.setItem(key, JSON.stringify({ data: result.data, expiresAt: Date.now() + ttl }));
    } catch { /* give up gracefully */ }
  }

  return result;
}

// ─── Cache management ────────────────────────────────────────────────────────

export function clearAllCache() {
  const keys = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const k = sessionStorage.key(i);
    if (k && k.startsWith(CACHE_PREFIX)) keys.push(k);
  }
  keys.forEach(k => sessionStorage.removeItem(k));
  return keys.length;
}

export function clearCacheForSection(sectionPrefix) {
  const keys = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const k = sessionStorage.key(i);
    if (k && k.startsWith(CACHE_PREFIX) && k.includes(sectionPrefix)) keys.push(k);
  }
  keys.forEach(k => sessionStorage.removeItem(k));
  return keys.length;
}

export function getCacheStats() {
  let total = 0, active = 0, expired = 0;
  for (let i = 0; i < sessionStorage.length; i++) {
    const k = sessionStorage.key(i);
    if (k && k.startsWith(CACHE_PREFIX)) {
      total++;
      try {
        const { expiresAt } = JSON.parse(sessionStorage.getItem(k));
        if (Date.now() < expiresAt) active++; else expired++;
      } catch { expired++; }
    }
  }
  return { total, active, expired };
}

// Evict oldest 20% of entries when storage is full
function evictOldestEntries() {
  const entries = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const k = sessionStorage.key(i);
    if (k && k.startsWith(CACHE_PREFIX)) {
      try {
        const { expiresAt } = JSON.parse(sessionStorage.getItem(k));
        entries.push({ key: k, expiresAt });
      } catch { sessionStorage.removeItem(k); }
    }
  }
  entries.sort((a, b) => a.expiresAt - b.expiresAt);
  entries.slice(0, Math.max(1, Math.ceil(entries.length * 0.2))).forEach(e => sessionStorage.removeItem(e.key));
}
